import { useAuthStore } from "../store/auth";

// In dev: Vite proxies /v1 → localhost:3023 (see vite.config.ts).
// In prod: nginx proxies /clearbooks/api → localhost:3023/v1.
const BASE = import.meta.env.PROD ? "/clearbooks/api" : "/v1";

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** Server-supplied error code, e.g. "duplicate_invoice_number". */
  code: string | null;
  /** Server-supplied human detail (overrides the auto message). */
  detail: string | null;
  constructor(status: number, body: unknown, message?: string) {
    const b = (body && typeof body === "object" ? body : {}) as {
      error?: string; detail?: string | { message?: string };
    };
    const code = typeof b.error === "string" ? b.error : null;
    const detail = typeof b.detail === "string"
      ? b.detail
      : (b.detail && typeof b.detail === "object" && typeof b.detail.message === "string"
          ? b.detail.message
          : null);
    super(message ?? detail ?? code ?? `API error ${status}`);
    this.status = status;
    this.body = body;
    this.code = code;
    this.detail = detail;
  }
}

/** Thrown when a write is attempted while the browser reports it's offline. */
export class OfflineError extends Error {
  constructor() {
    super("You're offline — this change can't be saved until you reconnect.");
    this.name = "OfflineError";
  }
}

/** Thrown when the server returns 402 trial_expired. */
export class TrialExpiredError extends Error {
  constructor() {
    super("Your 30-day free trial has ended. Contact hello@clearai.lk to continue.");
    this.name = "TrialExpiredError";
  }
}

const WRITE_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

async function request<T>(
  path: string,
  init: RequestInit = {},
  retried = false
): Promise<T> {
  // Fail writes fast (and with a human message) when we know we're offline.
  // GETs still go through — the service worker may serve a cached response.
  const method = (init.method ?? "GET").toUpperCase();
  if (WRITE_METHODS.has(method) && typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new OfflineError();
  }

  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers });
  } catch (e) {
    // fetch throws TypeError on network failure — translate to OfflineError
    // for writes so the UI message is friendly. For GETs, rethrow (the SW
    // may have already served a cached response, so we only get here when
    // there's genuinely nothing cached).
    if (WRITE_METHODS.has(method)) throw new OfflineError();
    throw e;
  }
  if (res.status === 401 && !retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, init, true);
  }
  if (res.status === 402) {
    // Trial expired — surface a distinguishable error so callers can display
    // the right copy. The TrialBanner is already showing in the chrome.
    throw new TrialExpiredError();
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function withHeaders(extra?: Record<string, string>): HeadersInit {
  return extra ?? {};
}

async function tryRefresh(): Promise<boolean> {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      clear();
      return false;
    }
    const data = (await res.json()) as { access_token: string; refresh_token: string };
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    clear();
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, extraHeaders?: Record<string, string>) =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      headers: withHeaders(extraHeaders),
    }),
  patch: <T>(path: string, body?: unknown, extraHeaders?: Record<string, string>) =>
    request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
      headers: withHeaders(extraHeaders),
    }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  // FormData uploads — multer reads from req.file, so we must NOT set
  // Content-Type ourselves (boundary handling). Only adds Authorization.
  postFile: async <T>(path: string, file: File): Promise<T> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = useAuthStore.getState().accessToken;
    const res = await fetch(`${BASE}${path}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { /* ignore */ }
      throw new ApiError(res.status, body);
    }
    return (await res.json()) as T;
  },
};

/**
 * Trigger a file download from an authenticated API endpoint.
 * Browsers don't send Authorization on a plain `<a download>`, so we fetch
 * the blob and synth a click on a temporary anchor.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, await res.text(), `Download ${path} failed`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Open an authenticated PDF (or other file) in a new browser tab.
 *
 * Plain <a target="_blank"> opens the URL directly, but our API endpoints
 * require a Bearer token in the Authorization header — the browser won't
 * send it on a top-level navigation, so the request hits the API
 * unauthenticated and returns 401 (Shamil's "can't create PDFs" bug).
 * Fetch the response with auth, build a blob URL, then open that.
 */
export async function viewFileInNewTab(path: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Callers pass either "/attachments/…" (bare) or "/v1/attachments/…" (from
  // backend download_url serialisers). In prod BASE is "/clearbooks/api"
  // which nginx maps to "/v1/…" — so if the caller already prefixed /v1 we
  // strip it before appending BASE, else nginx sees /clearbooks/api/v1/… →
  // /v1/v1/… → 404.
  const normalized = path.replace(/^\/v1/, "");
  const res = await fetch(`${BASE}${normalized}`, { headers });
  if (!res.ok) throw new ApiError(res.status, await res.text(), `Open ${path} failed`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  // window.open returns null when the popup blocker fires (e.g. when this
  // function isn't called inside a direct click handler). Fall back to a
  // synthetic anchor click so the user still gets the file.
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Release the blob URL after the new tab has had time to load.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
